import { useQuery } from '@tanstack/react-query';
import {
  useDataProvider,
  useInfiniteGetList,
  ResourceContextProvider,
} from 'react-admin';
import {
  PropsWithChildren,
  createContext,
  useMemo,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import {
  BloxCustomDataProviderType,
  SyndicationDriverItem,
} from 'utility/types/dataProvider';
import { useMaybeDuplicateChannel } from 'screens/syndication/hooks/useMaybeDuplicateChannel';
import { NoAccess } from 'components/NoAccess/NoAccess';
import { useCosmos } from 'providers/contexts/CosmosContext';
import {
  checkSiteAdminPermission,
  checkHasAnySyndicationPermission,
} from 'utility/permissions';
import { SearchContextProvider } from 'components/Search/SearchContextProvider';
import { CosmosEditorialContextProvider } from 'providers/contexts/CosmosEditorialContext';
import { ChannelsSearchView } from './ChannelsSearchView';
import { SavedSearchContextProvider } from 'components/SavedSearch/SavedSearchContextProvider';

// ---------- INTERFACES / TYPES / CONSTS ---------- //
interface ChannelsSearchContextType {
  driverData: SyndicationDriverItem[];
  syndicationDrivers: ReturnType<typeof useInfiniteGetList>;
  driverConfiguration?: ReturnType<typeof useQuery>;
  dialogOpen: boolean;
  selectedDriver: string | null;
  toggleDialog: () => void;
  setSelectedDriver: (driver: string | null) => void;
  maybeDuplicateChannel: (values: { [key: string]: any }) => Promise<boolean>;
  closeForm: () => void;
}

// ---------- SEARCH CONTEXT PROVIDER  ---------- //
export const ChannelsSearchContext = createContext<ChannelsSearchContextType | undefined>(
  undefined,
);

export const useChannelsSearch = () => {
  const context = useContext(ChannelsSearchContext);
  if (!context) {
    throw new Error(
      'useChannelsSearch must be used within a ChannelsSearchContextProvider',
    );
  }
  return context;
};

export const ChannelsSearchContextProvider = ({
  children,
}: PropsWithChildren): JSX.Element => {
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();
  const maybeDuplicateChannel = useMaybeDuplicateChannel();
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [driverData, setDriverData] = useState<SyndicationDriverItem[]>([]);
  const [selectedDriver, setSelectedDriver] = useState<string | null>(null);
  const [driverConfiguration, setDriverConfiguration] = useState<any>();

  const syndicationDrivers = useInfiniteGetList(
    'wire/driver',
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'label', order: 'ASC' },
    },
    { staleTime: Infinity, refetchOnWindowFocus: false },
  );

  const toggleDialog = useCallback(() => {
    setDialogOpen((prev) => !prev);
  }, []);

  const closeForm = useCallback(() => {
    setDialogOpen(false);
    setSelectedDriver(null);
    setDriverConfiguration(undefined);
  }, []);

  useEffect(() => {
    if (syndicationDrivers.data) {
      const newDriverData = syndicationDrivers.data.pages
        .map((page: any) => page.data)
        .flat();
      setDriverData(newDriverData);
    }
  }, [syndicationDrivers.data]);

  useEffect(() => {
    const fetchDriverConfig = async () => {
      if (selectedDriver) {
        try {
          const response = await dataProvider.getOne('wire/driver/config', {
            id: selectedDriver as string,
          });
          if (response.data.status === 'success') {
            setDriverConfiguration(response.data.data);
          }
        } catch (error) {
          console.error('Error fetching driver configuration:', error);
        }
      }
    };

    fetchDriverConfig();
  }, [selectedDriver, dataProvider]);

  const contextValue = useMemo(
    () => ({
      driverData,
      syndicationDrivers,
      driverConfiguration,
      setSelectedDriver,
      selectedDriver,
      dialogOpen,
      toggleDialog,
      closeForm,
      maybeDuplicateChannel,
    }),
    [
      driverData,
      syndicationDrivers,
      driverConfiguration,
      setSelectedDriver,
      selectedDriver,
      dialogOpen,
      toggleDialog,
      closeForm,
      maybeDuplicateChannel,
    ],
  );

  return (
    <ChannelsSearchContext.Provider value={contextValue}>
      {children}
    </ChannelsSearchContext.Provider>
  );
};

type ChannelSearchWithContextProps = {
  dashboard?: string;
};

export const ChannelSearchWithContext = ({
  dashboard,
}: ChannelSearchWithContextProps) => {
  const { sessionConfig } = useCosmos();

  const { granted: userIsAdmin } = checkSiteAdminPermission(sessionConfig);
  const { granted: userHasSyndication, name: syndicationPermName } =
    checkHasAnySyndicationPermission(sessionConfig);
  const hasWireSyndication = Boolean(userIsAdmin || userHasSyndication);

  if (!hasWireSyndication) {
    return <NoAccess permName={syndicationPermName} />;
  }

  /**
   * Need to override the RA resource here
   * from 'feeds' to 'wire/channel/search'
   * because our client side routing is not
   * 1-to-1 with the API endpoints, and react-admin
   * creates routes based on the resource name.
   *
   * InfiniteListBase and other react-admin components
   * can take a resource prop to override the resource, but
   * since we have components that target that resource
   * above the InfiniteListBase (e.g. SearchContextProvider),
   * we need to wrap the entire search view in a
   * ResourceContextProvider to ensure that the
   * resources are matched up properly above and below the
   * actual InfiniteListBase component.
   *
   * This is to be able to properly use the helpers like
   * getSearchItemInfo, getResourceDetails, determineUniqueId, etc...
   */

  return (
    <CosmosEditorialContextProvider dashboard={dashboard}>
      <ResourceContextProvider value="wire/channel">
        <ChannelsSearchContextProvider>
          <SavedSearchContextProvider>
            <SearchContextProvider>
              <ChannelsSearchView />
            </SearchContextProvider>
          </SavedSearchContextProvider>
        </ChannelsSearchContextProvider>
      </ResourceContextProvider>
    </CosmosEditorialContextProvider>
  );
};

export default ChannelSearchWithContext;
